import Component from '@glimmer/component'
import { service } from '@ember/service'
import { use } from 'ember-resources'
import { keepLatest } from 'reactiveweb/keep-latest'
import { trackedFunction } from 'reactiveweb/function'
import FidgetSpinnerWaveComponent from '@blakeelearning/fidget/components/fidget/spinner/wave'
import type WritingLegendsCatalogueService from 'teacher-ui-client/services/writing-legends-catalogue'
import { LongWritesCatalogueCard } from 'teacher-ui-client/components/writing-legends/long-writes-catalogue-card'
import type { LongWritesFilterButton } from 'teacher-ui-client/subscription-type/long-writes/index/template'
import NoResultsMessage from 'teacher-ui-client/components/no-results-message'

export interface LongWritesCatalogueItem {
  activity_id: string
  category: string
  grade_tag: string
  title: string
  description: string
}

interface Signature {
  Args: {
    gradePositionFilter?: LongWritesFilterButton['value']
    textFilter?: Nullable<string>
  }
}

export interface LongWritesCatalogueData {
  items: LongWritesCatalogueItem[]
  versionHash?: string
}

// todo: This mapping will be updated
const gradePositionTags: Record<LongWritesFilterButton['value'], string> = {
  '3,4': 'Grade0-5',
  '5,6,7+': 'Grade6+',
}

export class LongWritesCatalogueList extends Component<Signature> {
  @service writingLegendsCatalogue!: WritingLegendsCatalogueService

  fetchData = trackedFunction(this, () => {
    const url = this.writingLegendsCatalogue.buildUrlLongWrites()
    return this.writingLegendsCatalogue.fetchData<LongWritesCatalogueData>(url)
  })

  @use
  latestData = keepLatest({
    value: () => this.fetchData.value,
    when: () => this.fetchData.isPending,
  })

  get longWritesCatalogue() {
    return this.latestData?.items ?? []
  }

  get filteredLongWritesCatalogue() {
    let filteredCatalogue = this.longWritesCatalogue

    if (this.args.gradePositionFilter) {
      const allowedTags = gradePositionTags[this.args.gradePositionFilter]
      filteredCatalogue = filteredCatalogue.filter((item) => item.grade_tag.split(',').includes(allowedTags))
    }

    if (this.args.textFilter) {
      const textFilterLower = this.args.textFilter.toLowerCase()
      filteredCatalogue = filteredCatalogue.filter(
        (item) =>
          item.title.toLowerCase().includes(textFilterLower) ||
          item.description.toLowerCase().includes(textFilterLower),
      )
    }

    return filteredCatalogue
  }

  <template>
    {{#if this.fetchData.isPending}}
      <FidgetSpinnerWaveComponent @centered={{true}} />
    {{else}}
      <div class="flex flex-wrap items-stretch justify-center gap-x-8 gap-y-12">
        {{#each this.filteredLongWritesCatalogue as |longWrite|}}
          <LongWritesCatalogueCard
            @activity_id={{longWrite.activity_id}}
            @title={{longWrite.title}}
            @description={{longWrite.description}}
          />
        {{else}}
          <NoResultsMessage />
        {{/each}}
      </div>
    {{/if}}
  </template>
}
